import { useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useAgentPortalGuard } from "./useAgentPortalGuard.js";
import AgentPortalGuardState from "./AgentPortalGuardState.jsx";
import { useOwningAgentProfile } from "./useOwningAgentProfile.js";
import { useAgentApplicationsCount } from "./useAgentApplicationsCount.js";
import { useAgentApplicantDetail } from "./useAgentApplicantDetail.js";
import AgentWorkerPanel from "./AgentWorkerPanel.jsx";
import AgentEmployerPanel from "./AgentEmployerPanel.jsx";
import { isJobDemandSide } from "../workerLine/workerLineShared.js";
import { ApplicantDetailContent } from "./ApplicantDetailDrawer.jsx";
import OrgNode from "../workerLine/OrgNode.jsx";
import OrgConnectorLayer from "../workerLine/OrgConnectorLayer.jsx";
import "../workerLine/workerLine.css";
import "./agentOrgChart.css";
import "../pages/states.css";

import PageSkeleton from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Agent", "Org Chart"];
const APPLICANT_DRAWER_TITLE_ID = "aoc-applicant-drawer-title";

function describeError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูผังสายงานนี้";
  }
  return "ไม่สามารถโหลดผังสายงานได้ กรุณาลองใหม่อีกครั้ง";
}

/**
 * /backoffice/agent/org-chart - Prompt 138's Agent Portal Org Chart. This is
 * the signed-in Agent's own real branch context (their real parent Super
 * Agent + parent PLC + themselves - no siblings, since GetAgentProfileAsync
 * only ever returns visible agents for the caller's own scope, and an Agent
 * scope's visible-agent set is exactly {themselves}), never a fake tree.
 * Calls the exact same real GET /api/backoffice/agents/{agentId}/profile
 * endpoint the Invite Card page's own scope pill already uses
 * (./useOwningAgentProfile.js) - no new backend endpoint. Reuses
 * ../workerLine/OrgNode.jsx and ../workerLine/OrgConnectorLayer.jsx
 * unmodified (Prompt 137/137R's enterprise network-board components) for
 * the same visual language, rendered as a simple 3-tier vertical chain
 * (never a table, never the plain left-stacked debug tree Prompt 137
 * retired).
 *
 * Prompt 139D "Full Canvas + Scalable Worker View" redesign: owner feedback
 * was that the old 5-tile KPI strip (Invite/Applications/Worker/Employer/
 * Job) ate the canvas's own vertical space before a single node was even
 * visible. That strip is gone - Worker/Invite/Applications stay visible as
 * a single compact inline stat line in the header instead (still real,
 * still separately-counted, per Prompt 138W-R2's "never merge Worker and
 * Application counts" rule), and the canvas panel now gets the page's
 * remaining height. The old fixed pageSize:50 pipeline lane (rendered
 * unconditionally, unpaged, unsearchable) is replaced by
 * ./AgentWorkerPanel.jsx - a real page/search-driven roster over the same
 * endpoint (see its own doc comment) built to stay exactly one page's worth
 * of DOM rows regardless of how large the Agent's real roster is.
 */
export default function AgentOrgChartPage() {
  const guard = useAgentPortalGuard();
  const { data: identity } = useBackofficeIdentity();
  const profileState = useOwningAgentProfile(guard.status === "ready" ? guard.agentId : null);
  // Prompt 139D: a cheap count-only pull (pageSize:1) for the header/node
  // stat line only - AgentWorkerPanel below runs its own independent,
  // paged/searched pull of the same real list for the actual roster.
  const applicationsTotal = useAgentApplicationsCount(guard.status === "ready", { pageSize: 1 });
  const [selectedApplicationId, setSelectedApplicationId] = useState(null);
  const applicantDetailState = useAgentApplicantDetail(selectedApplicationId);

  if (guard.status !== "ready") {
    return <AgentPortalGuardState crumbs={CRUMBS} guard={guard} />;
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-aoc-page">
        <header className="wx-aoc-head">
          <h1>Org Chart ของฉัน</h1>
          <div className="wx-aoc-head-right">
            {profileState.status === "ready" && profileState.data && (
              <div className="wx-aoc-inline-stats">
                {isJobDemandSide(profileState.data.organizationSide) ? (
                  <>
                    {/* P156: the job side counts employers and jobs. Its
                        worker/application/invite counts are structurally
                        always zero, so printing them would describe an empty
                        worker line rather than a healthy employer one. */}
                    <Link to="/backoffice/thailand-jobs/employers" className="wx-aoc-inline-stat-link">
                      นายจ้าง <strong>{profileState.data.employerCount}</strong>
                    </Link>
                    <span className="wx-aoc-inline-dot" aria-hidden="true" />
                    <Link to="/backoffice/thailand-jobs/jobs" className="wx-aoc-inline-stat-link">
                      งาน <strong>{profileState.data.jobCount}</strong>
                    </Link>
                  </>
                ) : (
                  <>
                    <span>
                      Worker Profile <strong>{profileState.data.workerCount}</strong>
                    </span>
                    <span className="wx-aoc-inline-dot" aria-hidden="true" />
                    <Link to="/backoffice/agent/applications" className="wx-aoc-inline-stat-link">
                      ใบสมัคร <strong>{applicationsTotal.status === "ready" ? applicationsTotal.totalCount : "..."}</strong>
                    </Link>
                    <span className="wx-aoc-inline-dot" aria-hidden="true" />
                    <span>
                      คำเชิญ <strong>{profileState.data.inviteCount}</strong>
                    </span>
                  </>
                )}
              </div>
            )}
            {identity?.primaryRole && (
              <span className="wx-aoc-scope-pill">
                Scope <b>{identity.primaryRole}</b>
              </span>
            )}
          </div>
        </header>

        {profileState.status === "loading" && (
          <PageSkeleton variant="orgChart" label="กำลังโหลดผังสายงาน..." />
        )}

        {profileState.status === "error" && (
          <div className="wx-state-card wx-state-card--danger" role="alert">
            <p className="wx-state-body">{describeError(profileState.error)}</p>
          </div>
        )}

        {profileState.status === "ready" && profileState.data && (
          <>
            <section className="wx-panel wx-aoc-canvas-panel">
              <div className="wx-aoc-scroll">
                <AgentOrgChain profile={profileState.data} applicationsCount={applicationsTotal} />
              </div>
            </section>

            {isJobDemandSide(profileState.data.organizationSide) ? (
              <AgentEmployerPanel agentId={profileState.data.agentId} />
            ) : (
              <AgentWorkerPanel enabled onSelectApplication={setSelectedApplicationId} />
            )}

            <p className="wx-aoc-contacts-link">
              ข้อมูลติดต่อของสายงานคุณ: <Link to="/backoffice/agent/contacts">เปิดหน้า Contacts</Link>
            </p>
          </>
        )}
      </div>

      <DetailDrawer
        open={selectedApplicationId !== null}
        onClose={() => setSelectedApplicationId(null)}
        labelledBy={APPLICANT_DRAWER_TITLE_ID}
        selectableSelector=".wx-applicant-row, .wx-applicant-grid-row"
      >
        <ApplicantDetailContent
          titleId={APPLICANT_DRAWER_TITLE_ID}
          state={applicantDetailState}
          onProfileSaved={applicantDetailState.refetch}
        />
      </DetailDrawer>
    </AppShell>
  );
}

/**
 * Renders the Agent's own tier chain (parentSuperAgent -> parentPlc ->
 * self, either parent omitted honestly if the API didn't return one) using
 * ../workerLine/OrgNode.jsx/OrgConnectorLayer.jsx unmodified. Each
 * connector needs its own `.wx-oc-row` immediately-next sibling (that is
 * the exact DOM contract OrgConnectorLayer's real-DOM-measurement reads -
 * see its own doc comment), so every tier after the first is wrapped in a
 * `.wx-oc-branch` > `&lt;OrgConnectorLayer /&gt;` + `.wx-oc-row` pair even
 * though this chain is always exactly one node wide, never more.
 */
function AgentOrgChain({ profile, applicationsCount }) {
  const tiers = [];
  if (profile.parentSuperAgent) {
    tiers.push({ id: "superAgent", kind: "superAgent", name: profile.parentSuperAgent.organizationName || "-", code: profile.parentSuperAgent.organizationCode });
  }
  if (profile.parentPlc) {
    tiers.push({ id: "plc", kind: "plc", name: profile.parentPlc.organizationName || "-", code: profile.parentPlc.organizationCode });
  }
  // Prompt 138W-R2: Workers and Applications shown as two distinct, real
  // numbers on the node itself - never merged into one count, since a
  // Quick Registered application is not a Worker Profile.
  const applicationsLabel = applicationsCount.status === "ready" ? applicationsCount.totalCount : "...";
  // P156: what this Agent's line is actually made of. On the job side that is
  // employers and jobs; the worker/application/invite counts are structurally
  // always zero there and must not be shown as if the line were empty.
  const statLine = isJobDemandSide(profile.organizationSide)
    ? `นายจ้าง ${profile.employerCount} · งาน ${profile.jobCount}`
    : `Worker ${profile.workerCount} · ใบสมัคร ${applicationsLabel} · คำเชิญ ${profile.inviteCount}`;
  tiers.push({
    id: "agent",
    kind: "agent",
    name: profile.displayName,
    code: profile.agentCode,
    status: profile.agentStatus,
    statLine,
    selected: true,
  });

  const [firstTier, ...restTiers] = tiers;

  return (
    <div className="wx-aoc-chain">
      <OrgNode {...firstTier} />
      {restTiers.map(({ id, ...tierProps }) => (
        <div className="wx-oc-branch wx-aoc-branch" key={id}>
          <OrgConnectorLayer />
          <div className="wx-oc-row">
            <OrgNode {...tierProps} />
          </div>
        </div>
      ))}
    </div>
  );
}

